<script lang="ts">
  import Disclosure from '../ui/Disclosure.svelte';
  import WordChip from '../ui/WordChip.svelte';
  import ThemeIcon from '../themes/ThemeIcon.svelte';
  import type { SessionRoundResults } from '../session/room-session-state';
  import type { WordView } from '../../shared/results';
  import type { Placing, RevealStage } from './reveal-stages';

  /**
   * One post-match standings row: rank (a medal crown on the podium), identity, running score and,
   * at standings, a disclosure of the player's words. The list owns order, motion and row framing.
   */
  interface Props {
    player: SessionRoundResults['players'][number];
    /** Reserves the rank column through the recap so rows do not shift when ranks appear. */
    rankColumn: boolean;
    /** Shown once the recap finishes: a room rank (crowned on the podium), tier or Daily place. */
    placing: Placing | null;
    stage: RevealStage;
    you: boolean;
    /** The running score shown now; the final score at standings. */
    score: number;
    /** Classic scoring marks shared words with dashed chips. */
    classicScoring: boolean;
    /** This player's chip currently shown on the board, if any. */
    selectedWord: string | null;
    /** Toggles one of this player's chips on the board. */
    selectWord: (word: Readonly<WordView>) => void;
  }
  let { player, rankColumn, placing, stage, you, score, classicScoring, selectedWord, selectWord }: Props =
    $props();
  const podium = $derived(placing?.kind === 'rank' ? placing.podium : null);
</script>

{#snippet head()}<span class="lb-head">
    {#if placing?.kind === 'tier'}<span class="placing-badge">{placing.label}</span
      >{:else if placing?.kind === 'daily'}<span
        class="placing-badge"
        title="Place on this Daily's leaderboard">{placing.rank}</span
      >{:else if rankColumn}<span class="lb-rank" class:podium={podium !== null} data-podium={podium}
        >{#if placing?.kind === 'rank'}{#if podium}<ThemeIcon name="crown" monochrome /><span
              class="rank-number">{placing.rank}</span
            >{:else}{placing.rank}{/if}{/if}</span
      >{/if}<span class="lb-av">{player.avatar}</span><span class="lb-name"
      >{player.name}{#if you}<span class="sr-only"> (you)</span>{/if}{#if player.bot}<span class="bot-badge"
          >bot</span
        >{/if}<small>{player.words.length} word{player.words.length === 1 ? '' : 's'}</small></span
    ><span class="lb-score">{score}<small>pts</small></span>
  </span>{/snippet}
{#if stage === 'standings'}
  <!-- Only the viewer's own words start expanded; every row toggles independently. -->
  <Disclosure id={`standing-${player.id}`} open={you} trigger={head}>
    {#if player.words.length}<div class="chips">
        {#each player.words as word (word.w)}<WordChip
            word={word.w}
            points={word.pts}
            duplicate={!!word.dup && classicScoring}
            pressed={word.w === selectedWord}
            onToggle={() => selectWord(word)}
          />{/each}
      </div>{:else}<div class="none">no words this round</div>{/if}
  </Disclosure>
{:else}
  {@render head()}
{/if}

<style>
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  /* Inside the disclosure toggle, the row head takes the full width. */
  .lb-head {
    flex: 1;
    min-width: 0;
  }
  /* Room for a crown beside every rank keeps names aligned down the standings. */
  .lb-rank {
    flex: none;
    width: 34px;
  }
  [data-podium='gold'] {
    --podium-color: var(--theme-podium-gold);
  }
  [data-podium='silver'] {
    --podium-color: var(--theme-podium-silver);
  }
  [data-podium='bronze'] {
    --podium-color: var(--theme-podium-bronze);
  }
  /* The pack's crown, tinted by medal, outlines the rank number sitting in its band. */
  .lb-rank.podium {
    display: grid;
    place-items: center;
    height: 34px;
    color: var(--podium-color);
  }
  .lb-rank.podium > :global(.theme-icon) {
    grid-area: 1 / 1;
    font-size: 34px;
  }
  .rank-number {
    grid-area: 1 / 1;
    padding-top: 9px;
    font-size: 12px;
    font-weight: 800;
  }
  /* A Competitive tier or Daily leaderboard place in the rank's place, styled like the bot badge. */
  .placing-badge {
    flex: none;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    background: var(--theme-surface);
    color: var(--theme-muted-text);
    border: 1px solid var(--theme-border);
  }
  .none {
    font-size: 13px;
    color: var(--theme-muted-text);
  }
</style>
